import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { addMessage } from "src/store/messages"
import {
  buyCosmetic,
  buyRace,
  claimDaily,
  getShop,
  selectCredits,
  selectCreditPacks,
  selectCurrency,
  selectDaily,
  selectGameEntryCost,
  selectCosmetics,
  selectPurchasesEnabled,
  selectRaceCatalogue,
  selectShopLoaded,
} from "src/store/economy"
import { selectGameConfig } from "src/store/common"
import { selectIsGuest } from "src/store/user"
import { describeBonus, raceNames, specialUnitFor, unitName } from "src/utils/races"
import { apiErrorLines } from "src/utils/apiErrors"
import { zoneNames } from "src/utils/battlefield"

import { avatars, races as raceImages, raceUnits, unitIcons } from "src/assets/images"
import { AVATAR } from "src/utils/cosmetics"
import { REACTION_SLOT, reactionEmoji, reactionImage, reactionText, reactionsIn } from "src/utils/reactions"
import Modal from "src/components/modals/Modal"
import Checkout from "src/components/Checkout"
import SaveGuestButton from "src/components/SaveGuestButton"

import "./ShopModal.css"

const formatCredits = (credits) => String(credits ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")

// Where an edition stands on a shelf; anything without one is plain and comes first.
const EDITION_RANK = { epic: 1, legendary: 2 }

/* The shop: classes bought with credits, credits bought with money.

   Two tabs rather than two modals because they are one decision — a player who cannot afford a
   class needs the other tab, and making them close one thing and open another is where the
   purchase is abandoned. Opening the Credits tab from a class they cannot afford is one click.

   Everything on screen is priced by the server (`GET /shop/`): the catalogue, what is owned, and
   whether real money works here at all. Nothing is computed locally, so the price on a button
   cannot drift from the price that will be charged. */
const ShopModal = ({ initialTab = "classes" }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const credits = useSelector(selectCredits)
  const daily = useSelector(selectDaily)
  const entryCost = useSelector(selectGameEntryCost)
  const catalogue = useSelector(selectRaceCatalogue)
  const packs = useSelector(selectCreditPacks)
  const currency = useSelector(selectCurrency)
  const purchasesEnabled = useSelector(selectPurchasesEnabled)
  const loaded = useSelector(selectShopLoaded)
  const cosmetics = useSelector(selectCosmetics)
  // What a class *does* comes from the same server config the simulator applies (`RACE_BONUSES`,
  // `valid_units_per_race`), exactly as the lobby's cards read it — so the shop cannot advertise a
  // bonus the game does not give, and a rules change reaches both screens at once.
  const gameConfig = useSelector(selectGameConfig)
  // A guest buys nothing until the account is saved (owner, 2026-10-02). The server refuses every
  // purchase (`GuestsCannotShop`); this shows why instead of a catalogue that would only be refused.
  const isGuest = useSelector(selectIsGuest)
  const raceBonuses = gameConfig?.rules_config?.RACE_BONUSES || {}
  const unitsPerRace = gameConfig?.valid_units_per_race || {}

  const [tab, setTab] = useState(initialTab)
  const [busyRace, setBusyRace] = useState(null)
  // One selection across both kinds of product: the checkout renders under whichever is chosen,
  // and two live provider iframes for a purchase nobody has decided on is two too many.
  const [selectedProduct, setSelectedProduct] = useState(null)

  // Loaded when the shop opens rather than at bootstrap: most sessions never open it. Re-fetched
  // on every open, because the balance moves in games the player has been in since last time.
  useEffect(() => {
    dispatch(getShop())
  }, [dispatch])

  // `apiErrorLines` gives `{key, text}` rows for a form to render; a toast wants one sentence, so
  // they are joined. The server's own wording is preferred over ours — it is the one that knows
  // *why* ("that class costs 750", "you already own that class"), and a generic fallback is only
  // reached when the request never got an answer.
  const report = (error, fallbackKey) => {
    const text = apiErrorLines(error)
      .map((line) => line.text)
      .join(" ")
    return dispatch(addMessage({ text: text || t(fallbackKey), type: "error" }))
  }

  const clickClaimDaily = () =>
    dispatch(claimDaily())
      .unwrap()
      .then((result) =>
        dispatch(
          addMessage({ text: t("shop.dailyClaimed", { credits: result.granted }), type: "success" })
        )
      )
      .catch((error) => report(error, "shop.dailyFailed"))

  const clickBuyRace = (race) => {
    setBusyRace(race.race)
    dispatch(buyRace({ race: race.race }))
      .unwrap()
      .then(() =>
        dispatch(
          addMessage({
            text: t("shop.classUnlocked", { race: raceNames[race.race] || race.race }),
            type: "success",
          })
        )
      )
      .catch((error) => report(error, "shop.classFailed"))
      .finally(() => setBusyRace(null))
  }

  const onPaid = (result) => {
    setSelectedProduct(null)
    // A payment still clearing is neither of the other two outcomes: they have paid, and
    // they have no credits. Saying "credits added" would be a lie they can check in one glance.
    if (result.pending) {
      dispatch(addMessage({ text: result.detail || t("shop.paymentPending"), type: "info" }))
      return
    }
    dispatch(
      addMessage({
        text: t("shop.creditsAdded", { credits: result.creditsAdded }),
        type: "success",
      })
    )
    // The capture answers with the new balance, but the catalogue's affordability greying is
    // computed from the same request that produced it — so re-read it, and a class the player can
    // now afford stops looking out of reach the moment they can buy it.
    dispatch(getShop())
  }

  const balance = (
    <div className="shop-balance">
      <div>
        <p className="section-label">{t("shop.yourCredits")}</p>
        <p className="shop-balance-amount">{formatCredits(credits)}</p>
      </div>
      <button
        type="button"
        className={daily?.claimable ? "" : "quiet"}
        onClick={clickClaimDaily}
        disabled={!daily?.claimable}
        title={daily?.claimable ? undefined : t("shop.dailyAlreadyClaimed")}
      >
        {daily?.claimable
          ? t("shop.claimDaily", { credits: daily.amount })
          : t("shop.dailyClaimedToday")}
      </button>
    </div>
  )

  /* A shop shows what it can sell you (owner, 2026-09-08): "classes, and icons and everything that
     appears in the shop should be the things not available. If they are available, exclude them."

     Free and already-owned are one case here, not two. Both come back `owned: true` from the
     server, neither has a price, and neither has a button — so on every shelf they were cards that
     could only be looked at. The Looks tab was the worst of it, six inert cards in nine, but the
     Classes tab had the same shape and so did the reactions. What a player already has is answered
     where it is used: the lobby's race picker and its seat icon list, which show everything owned,
     free ones included.

     `free` and `owned` are checked separately even though the server sets `owned` on the free ones
     too. They are different claims — "nothing to pay" and "you have it" — and a shelf that leant on
     them agreeing would put the whole free catalogue back on screen the day one of them stopped. */
  const forSale = (items) => (items || []).filter((item) => !item.free && !item.owned)
  // A shelf ends on its rare things (owner, 2026-10-09: "Cracked grave order should be the last
  // in the shop right now, as it's EPIC"): plain first, then epic, then legendary, the catalogue's
  // order kept within each (`sort` is stable).
  const byEdition = (items) => [...items].sort((a, b) => (EDITION_RANK[a.edition] ?? 0) - (EDITION_RANK[b.edition] ?? 0))

  const classesForSale = forSale(catalogue)
  const iconsForSale = forSale(cosmetics).filter((item) => item.slot !== REACTION_SLOT && item.slot !== AVATAR)
  // The player's picture (owner, 2026-10-08): sold here, worn from the profile.
  const avatarsForSale = forSale(cosmetics).filter((item) => item.slot === AVATAR && item.on_sale !== false)
  const reactionsForSale = byEdition(forSale(reactionsIn(cosmetics)))
  // Everything, everywhere, bought. Worth saying out loud rather than leaving two empty tabs.
  const soldOut =
    loaded && !classesForSale.length && !iconsForSale.length && !avatarsForSale.length && !reactionsForSale.length

  const classesForTeam = (team) => classesForSale.filter((entry) => entry.team === team)

  const classesTab = (
    <div className="shop-classes">
      {["human", "zombie"].map((team) => (
        // A side with nothing left to sell drops its heading too: a bare "HUMANS" over empty space
        // reads as a shelf that failed to load rather than one that is finished.
        classesForTeam(team).length === 0 ? null : (
        <div className="shop-team" key={team}>
          <p className="section-label">
            {team === "human" ? t("lobby.teamHumans") : t("lobby.teamZombies")}
          </p>
          <div className="shop-cards">
            {classesForTeam(team).map((entry) => {
                const affordable = entry.price !== null && credits !== null && credits >= entry.price
                const bonuses = Object.entries(raceBonuses[entry.race] || {})
                const specialUnit = specialUnitFor(entry.race, entry.team, unitsPerRace)
                const teamLabel = team === "human" ? t("lobby.teamHumans") : t("lobby.teamZombies")
                // The card wears the plate of the unit the class fields, so pressing it opens
                // *that unit's* showcase — the same one the roster opens — with the class named
                // above it and what the class does listed under the unit's own line (owner,
                // 2026-09-06: "the animation of the unit"). A free class wears its side's basic
                // body, which `raceUnits` names; `specialUnitFor` has nothing to say about it.
                const unit = specialUnit || raceUnits[entry.race]
                const unitStats = gameConfig?.[`${team}_config`]?.TROOPS?.[unit]
                const openShowcase = () =>
                  ModalService.show("ArtShowcaseModal", {
                    src: raceImages[entry.race],
                    title: unitName(unit),
                    kicker: `${raceNames[entry.race] || entry.race} · ${teamLabel}`,
                    subtitle: unitStats ? zoneNames(t, gameConfig, team, unit) : undefined,
                    lines: [
                      t(`game.units.abilities.${unit}`, { defaultValue: "" }),
                      ...(bonuses.length
                        ? bonuses.map(([key, value]) => describeBonus(key, value, entry.team))
                        : [t("shop.noBonuses")]),
                    ],
                    team,
                  })
                return (
                  <div className="shop-card" key={entry.race}>
                    <button type="button" className="art-zoomable shop-card-image-button" onClick={openShowcase} title={t("showcase.open")}>
                      <img src={raceImages[entry.race]} alt={t("showcase.open")} className="shop-card-image" />
                    </button>
                    <div className="shop-card-body">
                      <p className="shop-card-name">{raceNames[entry.race] || entry.race}</p>
                      <p className="shop-card-price">
                        {t("shop.priceInCredits", { credits: formatCredits(entry.price) })}
                      </p>
                      {/* What the class does, in the lobby's own wording. Nobody spends 1 000
                          credits on a name — and finding out what it bought them in the lobby
                          afterwards is where the refund request comes from. */}
                      <div className="shop-card-traits">
                        {bonuses.length ? (
                          bonuses.map(([key, value]) => (
                            <p className={`shop-card-bonus${value < 0 ? " negative" : ""}`} key={key}>
                              {describeBonus(key, value, entry.team)}
                            </p>
                          ))
                        ) : (
                          <p className="shop-card-bonus neutral">{t("shop.noBonuses")}</p>
                        )}
                        {specialUnit ? (
                          <p className="shop-card-unlock">
                            {t("shop.unlocks", { unit: unitName(specialUnit) })}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {affordable ? (
                      <button
                        type="button"
                        onClick={() => clickBuyRace(entry)}
                        disabled={busyRace === entry.race}
                      >
                        {t("shop.unlock")}
                      </button>
                    ) : (
                      /* Not disabled when it cannot be afforded — that is the moment the other tab
                         is worth offering, and a dead button offers nothing. It is drawn as a link
                         rather than a second full-width button: on a card whose point is the
                         portrait and the bonuses, the loudest thing on it was the one action that
                         does not buy anything. */
                      <button type="button" className="shop-card-nudge" onClick={() => setTab("credits")}>
                        {t("shop.getCredits")}
                      </button>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
        )
      ))}
      {loaded && !catalogue.length ? <p className="p-small">{t("shop.empty")}</p> : null}
      {loaded && catalogue.length && !classesForSale.length ? (
        <p className="shop-empty">{soldOut ? t("shop.everythingOwned") : t("shop.everyClassOwned")}</p>
      ) : null}
    </div>
  )

  /* The shop sells; it no longer dresses anybody (owner's call, 2026-09-04). Wearing a look became
     a *per-game* decision when seats got their own icon — you pick one in the lobby, where it has
     to differ from your teammates' — so an account-wide "equipped" here was a second, quieter
     answer to the same question, and the two could disagree about what you were about to play as.

     What buying does now is what buying does: you own it, and it turns up in the lobby's picker. */
  const clickBuyCosmetic = (cosmetic) =>
    dispatch(buyCosmetic({ cosmetic: cosmetic.id }))
      .unwrap()
      .then(() =>
        dispatch(
          addMessage({
            text: t(
              cosmetic.wearable === false
                ? "shop.reactionBought"
                : cosmetic.slot === AVATAR
                  ? "shop.avatarBought"
                  : "shop.lookBought",
              {
              name: cosmetic.label,
            }),
            type: "success",
          })
        )
      )
      .catch((error) => report(error, "shop.lookFailed"))
      .catch((error) => report(error, "shop.lookFailed"))

  /* The edition's tag (owner, 2026-10-09: the epic and legendary labels on every cosmetic that has
     one, not on the pictures alone), over the card's top-left corner. The pictures carry theirs
     over the picture's own corner instead, in `shop-avatar-frame`. */
  const editionTag = (item) =>
    item.edition ? (
      <span className={`shop-edition shop-edition-${item.edition}`}>{t(`shop.edition_${item.edition}`)}</span>
    ) : null

  // The reactions, which share the Looks tab because they are the same kind of purchase and the
  // same promise — but they are neither per side nor worn, so they get their own list rather than
  // a card with a dead Wear button on it.
  const reactionsSection = reactionsForSale.length === 0 ? null : (
    <div className="shop-team">
      <p className="section-label">{t("shop.reactionsHeading")}</p>
      {/* The same card and the same grid the icons above use (owner, 2026-09-10). They were a row
          of their own shape — glyph and name on one line, the price pushed to the right edge, a
          small button under it — so the tab read as two shelves that had been stacked rather than
          one shop, and no two prices on it lined up. What is sold here is the same kind of thing
          at the same kind of price; it should be the same card, with the picture on top, the name
          and price under it, and the action across the bottom.

          Reactions keep their own list rather than joining the icons' grid: an icon is per side
          and a reaction belongs to nobody, so they are not one shelf to browse. */}
      <div className="shop-cards">
        {reactionsForSale.map((reaction) => {
          const affordable = credits !== null && credits >= (reaction.price ?? 0)
          return (
            <div className={`shop-card${reaction.edition ? ` shop-card-${reaction.edition}` : ""}`} key={reaction.id}>
              {editionTag(reaction)}
              {reactionImage(reaction) ? (
                <img
                  className="shop-card-image shop-reaction-image"
                  src={reactionImage(reaction)}
                  alt={reactionText(reaction)}
                />
              ) : (
                /* No artwork to show, so the glyph is the picture — the card cannot lead with an
                   empty tile where every other card on this tab leads with what it sells. */
                <span className="shop-card-image shop-reaction-emoji" aria-hidden="true">
                  {reactionEmoji(reaction)}
                </span>
              )}
              <div className="shop-card-body">
                <p className="shop-card-name">{reactionText(reaction)}</p>
                {/* The price is a line on the card and the button is the action, the same split
                    the class and look cards use. Folding the two together — a button reading "100
                    credits" that buys when pressed — worked only while the player could afford it:
                    the moment they could not, the button became the nudge and took the price off
                    the card with it, so the one reaction worth saving up for was the one that
                    would not say what it cost. */}
                <p className="shop-card-price">
                  {t("shop.priceInCredits", { credits: formatCredits(reaction.price) })}
                </p>
              </div>
              {affordable ? (
                <button type="button" onClick={() => clickBuyCosmetic(reaction)}>
                  {t("shop.buyLook")}
                </button>
              ) : (
                <button type="button" className="shop-card-nudge" onClick={() => setTab("credits")}>
                  {t("shop.getCredits")}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  // The pictures, before the map icons: a picture is seen by everybody who opens the ranking, and
  // it belongs to no side, so it is one shelf rather than two.
  const avatarsSection = avatarsForSale.length === 0 ? null : (
    <div className="shop-team">
      <p className="section-label">{t("shop.avatarsHeading")}</p>
      <p className="p-small">{t("shop.avatarsPitch")}</p>
      <div className="shop-cards">
        {avatarsForSale.map((avatar) => {
          const affordable = credits !== null && credits >= (avatar.price ?? 0)
          return (
            <div className={`shop-card${avatar.edition ? ` shop-card-${avatar.edition}` : ""}`} key={avatar.id}>
              <span className="shop-avatar-frame">
                <img src={avatars[avatar.id]} alt={avatar.label} className="shop-card-image shop-avatar-image" />
                {/* A limited, epic or legendary picture says so on its face: what it is is why it costs
                    what it does (one credit for the beta, more for one that moves). */}
                {avatar.edition ? (
                  <span className={`shop-edition shop-edition-${avatar.edition}`}>
                    {t(`shop.edition_${avatar.edition}`)}
                  </span>
                ) : null}
              </span>
              <div className="shop-card-body">
                <p className="shop-card-name">{avatar.label}</p>
                <p className="shop-card-price">
                  {avatar.price === 1
                    ? t("shop.priceOneCredit")
                    : t("shop.priceInCredits", { credits: formatCredits(avatar.price) })}
                </p>
              </div>
              {affordable ? (
                <button type="button" onClick={() => clickBuyCosmetic(avatar)}>
                  {t("shop.buyLook")}
                </button>
              ) : (
                <button type="button" className="shop-card-nudge" onClick={() => setTab("credits")}>
                  {t("shop.getCredits")}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  const iconsForTeam = (team) => byEdition(iconsForSale.filter((icon) => icon.team === team))
  const nothingLeftHere = !iconsForSale.length && !avatarsForSale.length && !reactionsForSale.length

  const looksTab = (
    <div className="shop-looks">
      {avatarsSection}
      {iconsForSale.length > 0 && <p className="section-label">{t("shop.iconsHeading")}</p>}
      {nothingLeftHere && (
        <p className="shop-empty">{soldOut ? t("shop.everythingOwned") : t("shop.everyLookOwned")}</p>
      )}
      {["human", "zombie"].map((team) => (
        iconsForTeam(team).length === 0 ? null : (
        <div className="shop-team" key={team}>
          <p className="section-label">
            {team === "human" ? t("lobby.teamHumans") : t("lobby.teamZombies")}
          </p>
          <div className="shop-cards">
            {iconsForTeam(team).map((cosmetic) => {
                const affordable = credits !== null && credits >= (cosmetic.price ?? 0)
                return (
                  <div className={`shop-card${cosmetic.edition ? ` shop-card-${cosmetic.edition}` : ""}`} key={cosmetic.id}>
                    {editionTag(cosmetic)}
                    <img src={unitIcons[cosmetic.id]} alt={cosmetic.label} className="shop-card-image" />
                    <div className="shop-card-body">
                      <p className="shop-card-name">{cosmetic.label}</p>
                      <p className="shop-card-price">
                        {t("shop.priceInCredits", { credits: formatCredits(cosmetic.price) })}
                      </p>
                    </div>
                    {/* Owning it is the end of it here. Which look you *play* as is chosen in the
                        lobby, per game, against what your teammates have taken. */}
                    {affordable ? (
                      <button type="button" onClick={() => clickBuyCosmetic(cosmetic)}>
                        {t("shop.buyLook")}
                      </button>
                    ) : (
                      <button type="button" className="shop-card-nudge" onClick={() => setTab("credits")}>
                        {t("shop.getCredits")}
                      </button>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
        )
      ))}
      {reactionsSection}
    </div>
  )

  const creditsTab = (
    <div className="shop-credits">
      {/* A server with no payment credentials is a supported state, and on it this tab said "this
          server is not set up to sell credits" and then listed two packs with prices in euros
          under it. Disabled is not the same as absent: a price on screen is an offer, and an offer
          the server will answer with a 503 is a contradiction the player is left to resolve. The
          line above says what still works; the shelf comes down with the till. */}
      {purchasesEnabled ? null : (
        <p className="p-small shop-warning">{t("shop.purchasesUnavailable")}</p>
      )}
      {purchasesEnabled && (
        <div className="shop-packs">
          {packs.map((pack) => (
            <button
              type="button"
              key={pack.id}
              className={`shop-pack${selectedProduct?.id === pack.id ? " picked" : ""}`}
              onClick={() => setSelectedProduct(pack)}
            >
              <p className="shop-pack-credits">{formatCredits(pack.totalCredits)}</p>
              <p className="p-mini">{t("shop.credits")}</p>
              {pack.bonus ? (
                <p className="shop-pack-bonus">
                  {/* Grouped like every other number on this sheet. It was the one credit figure
                      printed raw, which went unnoticed while the only bonus was 250 — the first
                      four-digit one read "+5000" under a "10 000". */}
                  {t("shop.bonus", { credits: formatCredits(pack.bonus) })}
                </p>
              ) : null}
              <p className="shop-pack-price">
                {pack.price} {currency}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* The checkout appears under the chosen pack rather than one per card: a provider renders
          a live iframe per button set, and four of them on screen is four things loading for a
          purchase the player has not decided on yet. */}
      {selectedProduct && purchasesEnabled ? (
        <div className="shop-checkout">
          <p className="p-small">
            {t("shop.buying", {
              credits: formatCredits(selectedProduct.totalCredits),
              price: `${selectedProduct.price} ${currency}`,
            })}
          </p>
          <Checkout
            product={selectedProduct}
            onPaid={onPaid}
            onFailed={() =>
              dispatch(addMessage({ text: t("shop.paymentFailed"), type: "error" }))
            }
          />
        </div>
      ) : null}
    </div>
  )

  return (
    <Modal width="min(1100px, 94vw)">
      <div className="shop">
        <header className="shop-head">
          <h2>{t("shop.title")}</h2>
          <div className="shop-tabs" hidden={isGuest}>
            <button
              type="button"
              className={tab === "classes" ? "" : "quiet"}
              onClick={() => setTab("classes")}
            >
              {t("shop.tabClasses")}
            </button>
            <button
              type="button"
              className={tab === "looks" ? "" : "quiet"}
              onClick={() => setTab("looks")}
            >
              {t("shop.tabLooks")}
            </button>
            <button
              type="button"
              className={tab === "credits" ? "" : "quiet"}
              onClick={() => setTab("credits")}
            >
              {t("shop.tabCredits")}
            </button>
          </div>
        </header>

        {balance}

        {isGuest ? (
          <div className="shop-guest">
            <p className="p-small">{t("guest.shopLocked")}</p>
            <SaveGuestButton />
          </div>
        ) : tab === "classes" ? (
          classesTab
        ) : tab === "looks" ? (
          looksTab
        ) : (
          creditsTab
        )}

        <div className="shop-actions">
          <button type="button" className="quiet" onClick={() => ModalService.hide()}>
            {t("shop.close")}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default ShopModal
